/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { AppState, NativeModules } from 'react-native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppConfig, useDeviceInfo } from '@airasia-rn/ui';
import {
  HOMEPAGE_BLOCK_IDS,
  type DynamicSplashData,
  type HomepageBlock,
  type HomepageBlockResponse,
  type NotificationBannerData,
  type Platform,
} from '../models/homepageBlockTypes';
import { usePlatform } from '../utils/platformDetection';
import { HomepageService } from '../services/homepageApiService';
import { FallbackHomepageApiService } from '../services/fallbackHomepageApiService';
import { useHomeConfig } from '../config/HomeConfigProvider';
import fallbackHomepageResponse from '../assets/data/fallbackHomepageResponse.json';
import { rewardsKeys } from './useRewardsProfile';
import { bigPayKeys } from './useBigPayBalance';
import { tripPlannerKeys } from './useUpcomingTrips';
import {
  createHomepageBlocksQueryOptions,
  homepageKeys,
} from './homepageQueryOptions';
import { useOptionalForcedPlatform } from '../providers/HomepageProvider';

const FALLBACK_CACHE_SEED_DELAY_MS = 2500;
const REFRESH_CONTROL_TIMEOUT_MS = 6000;
type RefreshAllOptions = {
  showRefreshControl?: boolean;
  beforeRefresh?: () => Promise<unknown>;
};

// Treat the homepage as renderable only when there are actual CMS blocks,
// so we do not clear the skeleton into an empty page.
const hasDisplayableHomepageData = (
  response?: HomepageBlockResponse | null,
): response is HomepageBlockResponse => Boolean(response?.data?.blocks?.length);

// Seed TanStack with the bundled homepage shape so the UI can render a safe
// fallback through the normal query path when startup network is too slow.
const getBundledHomepageFallback = (): HomepageBlockResponse => ({
  ...(fallbackHomepageResponse as unknown as HomepageBlockResponse),
  _isFallback: true,
});

export {
  createHomepageBlocksQueryOptions,
  homepageKeys,
} from './homepageQueryOptions';

export const useNotificationData = (options?: { platform?: Platform }) => {
  const { blocks } = useHomepageBlocks(options);

  return useMemo(() => {
    const block = blocks.find((b) => b.id === 'block_notification_banner');

    if (!block || !block.data) return null;

    const data = block.data as NotificationBannerData;

    if (!data.notificationBanner) return null;

    return {
      text: data.notificationBanner.text,
      actionTitle: data.notificationBanner.callToAction?.text,
      actionUrl: data.notificationBanner.callToAction?.url,
    };
  }, [blocks]);
};

export function useHomepageApiService(): HomepageService {
  const { config } = useHomeConfig();
  return useMemo(() => {
    return new HomepageService(config.homeCosmosApi, config.homeCosmosApiKey);
  }, [config.homeCosmosApi, config.homeCosmosApiKey]);
}

export function useFallbackHomepageApiService(): FallbackHomepageApiService {
  const appConfig = useAppConfig();
  return useMemo(
    () => new FallbackHomepageApiService(appConfig.config.env ?? 'stg'),
    [appConfig.config.env],
  );
}

/**
 * Pure API Hook: Fetches data without extra logic.
 */
export const useFetchHomePage = (
  platform: Platform,
  locale: string,
  geoId: string,
  enabled: boolean = true,
  currency?: string,
) => {
  const api = useHomepageApiService();
  const fallbackApi = useFallbackHomepageApiService();

  return useQuery({
    ...createHomepageBlocksQueryOptions(
      api,
      fallbackApi,
      platform,
      locale,
      geoId,
      currency,
    ),
    enabled,
  });
};

/**
 * Logic Hook: Consumes the fetch hook and adds sorting/filtering.
 */
export const useHomepageBlocks = (options?: {
  platform?: Platform;
  enabled?: boolean;
  sortByOrder?: boolean;
  onDynamicSplashDataReceived?: (data: DynamicSplashData) => void;
}) => {
  const detectedPlatform = usePlatform();
  const forcedPlatform = useOptionalForcedPlatform();
  const platform = options?.platform ?? forcedPlatform ?? detectedPlatform;
  const onDynamicSplashDataReceived = options?.onDynamicSplashDataReceived;
  const queryClient = useQueryClient();
  const appConfig = useAppConfig();
  const { isWeb } = useDeviceInfo();
  const locale = appConfig.config.locale || 'en-gb';
  const geoId = appConfig.config.geoId || 'MY';
  const currency = appConfig.config.currency;
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshControlTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const queryKey = homepageKeys.data(platform, locale, geoId, currency);

  const query = useFetchHomePage(
    platform,
    locale,
    geoId,
    // Web: the server prefetches the homepage, so the client only reads the
    // hydrated cache. Native: allow the query to mount even during uncertain
    // startup network so the API -> fallback API -> bundled fallback chain runs.
    options?.enabled ?? !isWeb,
    currency,
  );
  const homepageData = query.data;
  const refetchHomepage = query.refetch;

  useEffect(() => {
    if (homepageData?._isFallback || !onDynamicSplashDataReceived) return;

    const dynamicSplashBlock = homepageData?.data?.blocks?.find(
      (block) => block.id === HOMEPAGE_BLOCK_IDS.DYNAMIC_SPLASH,
    );
    const dynamicSplashData = dynamicSplashBlock?.data;

    if (dynamicSplashData && Array.isArray(dynamicSplashData.campaigns)) {
      onDynamicSplashDataReceived(dynamicSplashData);
    }
  }, [homepageData, onDynamicSplashDataReceived]);

  const clearRefreshControlTimeout = useCallback(() => {
    if (refreshControlTimeoutRef.current) {
      clearTimeout(refreshControlTimeoutRef.current);
      refreshControlTimeoutRef.current = null;
    }
  }, []);
  const stopRefreshControl = useCallback(() => {
    clearRefreshControlTimeout();
    setIsRefreshing(false);
  }, [clearRefreshControlTimeout]);

  useEffect(() => {
    if (hasDisplayableHomepageData(homepageData)) {
      return;
    }

    const timer = setTimeout(() => {
      // Let the live query or persisted cache win first, then seed bundled
      // fallback only if we still have nothing renderable.
      const cachedQueryData =
        queryClient.getQueryData<HomepageBlockResponse>(queryKey);

      if (hasDisplayableHomepageData(cachedQueryData)) {
        return;
      }

      queryClient.setQueryData(queryKey, getBundledHomepageFallback());
    }, FALLBACK_CACHE_SEED_DELAY_MS);

    return () => {
      clearTimeout(timer);
    };
  }, [homepageData, queryClient, queryKey]);

  const responseCurrency =
    query.isSuccess || query.isError
      ? query.data?.data?.currency || 'USD'
      : undefined;
  const refetchCurrency = (currency || responseCurrency || 'USD').toUpperCase();

  const getCompanionSectionRefetchTasks = useCallback(
    () => [
      queryClient.refetchQueries({
        queryKey: rewardsKeys.profile(refetchCurrency),
        type: 'all',
      }),
      queryClient.refetchQueries({
        queryKey: bigPayKeys.balance(),
        type: 'all',
      }),
      queryClient.refetchQueries({
        queryKey: tripPlannerKeys.upcomingTrip(locale, refetchCurrency),
        type: 'all',
      }),
      queryClient.refetchQueries({
        predicate: ({ queryKey }) =>
          queryKey.includes('avalon') &&
          queryKey.includes('orders') &&
          queryKey.includes('upcoming'),
        type: 'all',
      }),
    ],
    [locale, queryClient, refetchCurrency],
  );

  const refetchHomeSections = useCallback(
    async (refreshRecommendationFeeds?: () => Promise<void>) => {
      const refreshTasks: Promise<unknown>[] = [
        refetchHomepage(),
        ...getCompanionSectionRefetchTasks(),
      ];

      if (refreshRecommendationFeeds) {
        refreshTasks.push(refreshRecommendationFeeds());
      }

      await Promise.allSettled(refreshTasks);
    },
    [getCompanionSectionRefetchTasks, refetchHomepage],
  );

  useEffect(() => {
    if (!responseCurrency) return;
    if (!isWeb) {
      NativeModules.ReactToNative?.updateCurrency?.(responseCurrency);
    }
  }, [responseCurrency, isWeb]);

  useEffect(() => {
    if (isWeb) {
      return clearRefreshControlTimeout;
    }

    const subscription = AppState.addEventListener('change', (status) => {
      if (status !== 'active') {
        stopRefreshControl();
      }
    });

    return () => {
      subscription.remove();
      clearRefreshControlTimeout();
    };
  }, [clearRefreshControlTimeout, isWeb, stopRefreshControl]);

  const blocks = useMemo(() => {
    const rawBlocks = query.data?.data?.blocks || [];
    if (options?.sortByOrder) {
      return [...rawBlocks].sort((a, b) => (a.order || 0) - (b.order || 0));
    }
    return rawBlocks;
  }, [query.data, options?.sortByOrder]);

  /**
   * Refresh homepage blocks and the dependent sections rendered alongside them.
   */
  const refreshAll = useCallback(
    async (
      refreshRecommendationFeeds?: () => Promise<void>,
      refreshOptions?: RefreshAllOptions,
    ) => {
      const showRefreshControl = refreshOptions?.showRefreshControl ?? true;
      if (showRefreshControl) {
        if (refreshControlTimeoutRef.current) {
          clearTimeout(refreshControlTimeoutRef.current);
        }
        setIsRefreshing(true);
        // RefreshControl is controlled; always release it even if native work hangs.
        refreshControlTimeoutRef.current = setTimeout(
          stopRefreshControl,
          REFRESH_CONTROL_TIMEOUT_MS,
        );
      }

      try {
        await refreshOptions?.beforeRefresh?.();
        await refetchHomeSections(refreshRecommendationFeeds);
      } finally {
        if (showRefreshControl) {
          stopRefreshControl();
        }
      }
    },
    [refetchHomeSections, stopRefreshControl],
  );

  return {
    ...query,
    blocks,
    refreshAll,
    isRefreshing,
    responseCurrency,
  };
};

export const useHomepageBlock = <TBlock extends HomepageBlock = HomepageBlock>(
  blockId: string,
  options?: { platform?: Platform },
) => {
  const { blocks } = useHomepageBlocks(options);
  return useMemo(() => {
    return blocks.find((block) => block.id === blockId) as TBlock | undefined;
  }, [blocks, blockId]);
};

export const useHomepageMetadata = (options?: { platform?: Platform }) => {
  const { data } = useHomepageBlocks(options);
  return data?.metadata;
};
